// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { CYCLICAL_REFERENCE_ERROR_CODE } from '@/api/constants';
import { handleAPIError } from '@/api/error-utils';
import { EditablePropertyButton } from '@/components/editable-property-button';
import { PopoverForm } from '@/components/popover-form';
import { SupportSnackbar } from '@/components/support-snackbar';
import {
  AccountTreeRounded,
  InfoOutlined,
} from '@/constants/hardfin-standard-icons';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useTimeout } from '@/hooks/use-timeout';
import { Project } from '@/project/api/types';
import { FormProjectSearch } from '@/project/components/form-project-search';
import { ProjectRenewalCustomerWarning } from '@/project/components/project-renewal-customer-warning';
import { ProjectFilterCallback } from '@/project/components/project-search';
import { ProjectTreeDialog } from '@/project/components/project-tree-dialog';
import { useUpdateProject } from '@/project/hooks/use-update-project';
import { sparseCustomerSchema } from '@/sparse/api/types';
import { zodResolver } from '@hookform/resolvers/zod';
import { Stack, Tooltip, Typography, useTheme } from '@mui/material';
import { AxiosError } from 'axios';
import { reject } from 'lodash-es';
import { MouseEventHandler, useRef } from 'react';
import { SubmitHandler, useForm, useWatch } from 'react-hook-form';
import { z } from 'zod';

const projectSchema = z.object({
  id: z.string(),
  key: z.string(),
  name: z.string(),
  customer: sparseCustomerSchema.nullable(),
});

const formSchema = z.object({ project: projectSchema.nullable() });

type FormData = z.infer<typeof formSchema>;

type RenewableProject = Pick<
  Project,
  'id' | 'key' | 'name' | 'customer' | 'previousProject' | 'startOn' | 'endOn'
>;

interface Props {
  project: RenewableProject;
  onError?: (error: AxiosError | null) => void;
}

export function ProjectPreviousProjectControl(props: Props) {
  const theme = useTheme();
  const editor = useDisclosure();
  const treeDialog = useDisclosure();
  const ref = useRef<HTMLButtonElement>(null);

  const updateProject = useUpdateProject(props.project.key, {
    onSuccess: (project) => {
      reset({ project: project.previousProject });
      editor.close();
      props.onError?.(null);
    },
    onError: (error) => {
      props.onError?.(error);
    },
  });

  const { control, reset, handleSubmit } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: { project: props.project.previousProject },
  });

  const timeout = useTimeout(reset, theme.transitions.duration.short);
  const project = useWatch({ control, name: 'project' });

  const labelColor =
    props.project.previousProject === null ? 'text.secondary' : 'text.primary';

  const projectFilter: ProjectFilterCallback = (projects) => {
    return reject(projects, { key: props.project.key });
  };

  const handleClose = () => {
    editor.close();
    timeout.start();
  };

  const handleClickProjectTree: MouseEventHandler<SVGElement> = (event) => {
    treeDialog.open();
    event.stopPropagation();
  };

  const handleValidSubmission: SubmitHandler<FormData> = (data) => {
    if (data.project === null) {
      return updateProject.mutate({ previousProjectId: null });
    }

    updateProject.mutate({ previousProjectId: data.project.id });
  };

  return (
    <>
      <EditablePropertyButton ref={ref} onClick={editor.open}>
        <Typography
          display="flex"
          alignItems="center"
          variant="body2"
          color={labelColor}
        >
          {props.project.previousProject?.name ?? 'None'}
          <Tooltip arrow placement="right" title="View project tree">
            <AccountTreeRounded
              fontSize="inherit"
              color="action"
              sx={{ marginX: 1 }}
              onClick={handleClickProjectTree}
            />
          </Tooltip>
        </Typography>
      </EditablePropertyButton>
      <ProjectTreeDialog
        project={props.project}
        isOpen={treeDialog.isOpen}
        onClose={treeDialog.close}
      />
      <SupportSnackbar
        isOpen={(() => {
          if (!updateProject.isError) return false;
          const apiError = handleAPIError(updateProject.error);
          return apiError.apiStatusCode !== CYCLICAL_REFERENCE_ERROR_CODE;
        })()}
        onClose={() => {
          updateProject.reset();
          props.onError?.(null);
        }}
        message="An error occurred while updating the project."
      />
      <PopoverForm
        width="20rem"
        position="left"
        isOpen={editor.isOpen}
        anchorElement={ref.current}
        onCancel={handleClose}
        onSubmit={handleSubmit(handleValidSubmission)}
        alert={
          <ProjectRenewalCustomerWarning
            project={props.project}
            renewal={project}
          />
        }
      >
        <Stack direction="row" alignItems="center" paddingLeft={0.5}>
          <Typography variant="subtitle2">Previous project</Typography>
          <Tooltip
            title="Identifies the previous or 'parent' project for renewal tracking and project history, such as a project that was renewed, replaced, or extended by this project"
            placement="top"
            arrow
          >
            <InfoOutlined
              color="action"
              sx={{ marginLeft: 0.5, fontSize: '16px' }}
            />
          </Tooltip>
        </Stack>
        <FormProjectSearch
          name="project"
          filter={projectFilter}
          control={control}
        />
      </PopoverForm>
    </>
  );
}
